"use client";
import React, { useState, useRef } from "react";
import { FaCheck, FaTimes, FaSpinner } from "react-icons/fa";

const ContactSection = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal]       = useState(false);
  const formRef = useRef();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(formRef.current);
    const payload = {
      company_name: formData.get("company_name"),
      user_email:   formData.get("user_email"),
      phone:        formData.get("phone"),
      service_type: formData.get("service_type"),
      service_area: formData.get("service_area"),
      message:      formData.get("message"),
    };

    try {
      const res = await fetch("/api/contact-home", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Network response was not ok");

      setShowModal(true);
      formRef.current.reset();
    } catch (err) {
      console.error("Submission error:", err);
      alert("Oops—something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => setShowModal(false);

  return (
    <section
      className="relative min-h-screen bg-cover bg-center"
      style={{
        backgroundImage:
          "url('/image/contact-section-bg.webp')",
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-600/95 to-secondary-600/95 mix-blend-multiply" />

      <div className="relative flex flex-col min-h-screen justify-center py-8 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto w-full max-w-7xl flex flex-col lg:flex-row items-center justify-center lg:gap-8 xl:gap-12">
          {/* Left Content */}
          <div className="flex-1 text-white mb-12 lg:mb-0 lg:pr-4">
            <div className="max-w-2xl">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight mb-6">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary-400 to-secondary-400">
                  Grow Your Cleaning Business Online — The Simple Way
                </span>
              </h1>
              <p className="text-lg sm:text-xl font-light opacity-90 leading-relaxed mb-8">
                We help cleaning companies like yours get more local clients with
                websites that actually work — no tech skills needed.
              </p>
              <div className="space-y-3 sm:space-y-4 mb-8">
                {[
                  "24/7 Customer Support Team",
                  "30-Day Satisfaction Guarantee",
                  "Eco-Friendly Cleaning Focus",
                  "Instant Booking Conversions",
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center space-x-2">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary-500/20">
                      <FaCheck className="text-primary-400 text-xs" />
                    </div>
                    <span className="text-base">{item}</span>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-xl">
                {[
                  { icon: "⭐", text: "5‑Star Rated Service" },
                  { icon: "🔒", text: "Secure Payments" },
                  { icon: "📅", text: "Flexible Scheduling" },
                ].map((b, idx) => (
                  <div
                    key={idx}
                    className="p-3 bg-light-surface/10 rounded-lg backdrop-blur-sm border border-light-border/20 text-center"
                  >
                    <div className="text-2xl mb-2">{b.icon}</div>
                    <div className="text-xs opacity-75">{b.text}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div className="flex-1 lg:pl-4 w-full max-w-xl">
            <div className="relative bg-light-surface dark:bg-dark-surface rounded-xl shadow-lg p-6 border border-light-border dark:border-dark-border">
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-4 sm:-translate-y-6">
                <div className="bg-primary-500 text-white text-sm sm:text-base px-4 sm:px-6 py-1.5 sm:py-2 rounded-full font-semibold shadow-md">
                  Get Your Free Quote
                </div>
              </div>

              <form
                ref={formRef}
                onSubmit={handleSubmit}
                className="space-y-5 mt-6"
              >
                <div>
                  <label className="block mb-2 text-light-text-primary dark:text-dark-text-primary">
                    Company Name
                  </label>
                  <input
                    name="company_name"
                    type="text"
                    required
                    placeholder="Perfect Clean Co."
                    className="w-full px-4 py-3 rounded-lg border border-light-border dark:border-dark-border focus:border-primary-500 focus:ring-2 focus:ring-primary-200 bg-light-background dark:bg-dark-background text-light-text-primary dark:text-dark-text-primary "
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-2 text-light-text-primary dark:text-dark-text-primary">
                      Email Address
                    </label>
                    <input
                      name="user_email"
                      type="email"
                      required
                      placeholder="hello@company.com"
                      className="w-full px-4 py-3 rounded-lg border border-light-border dark:border-dark-border focus:border-primary-500 focus:ring-2 focus:ring-primary-200 bg-light-background dark:bg-dark-background text-light-text-primary dark:text-dark-text-primary "
                    />
                  </div>
                  <div>
                    <label className="block mb-2 text-light-text-primary dark:text-dark-text-primary">
                      Phone Number (Optional)
                    </label>
                    <input
                      name="phone"
                      type="tel"
                      placeholder="(555) 123‑4567"
                      className="w-full px-4 py-3 rounded-lg border border-light-border dark:border-dark-border focus:border-primary-500 focus:ring-2 focus:ring-primary-200 bg-light-background dark:bg-dark-background text-light-text-primary dark:text-dark-text-primary "
                    />
                  </div>
                </div>

                <div>
                  <label className="block mb-2 text-light-text-primary dark:text-dark-text-primary">
                    Service Type
                  </label>
                  <select
                    name="service_type"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-light-border dark:border-dark-border focus:border-primary-500 focus:ring-2 focus:ring-primary-200 bg-light-background dark:bg-dark-background text-light-text-primary dark:text-dark-text-primary"
                  >
                    <option value="">Select Primary Service</option>
                    <option>Residential Cleaning</option>
                    <option>Commercial Cleaning</option>
                    <option>Specialized Services</option>
                  </select>
                </div>

                <div>
                  <label className="block mb-2 text-light-text-primary dark:text-dark-text-primary">
                    Target Service Area
                  </label>
                  <input
                    name="service_area"
                    type="text"
                    required
                    placeholder="E.g., Greater Chicago Area"
                    className="w-full px-4 py-3 rounded-lg border border-light-border dark:border-dark-border focus:border-primary-500 focus:ring-2 focus:ring-primary-200 bg-light-background dark:bg-dark-background text-light-text-primary dark:text-dark-text-primary "
                  />
                </div>

                <div>
                  <label className="block mb-2 text-light-text-primary dark:text-dark-text-primary">
                    Message
                  </label>
                  <textarea
                    name="message"
                    rows="4"
                    required
                    placeholder="Tell us about your cleaning business needs..."
                    className="w-full px-4 py-3 rounded-lg border border-light-border dark:border-dark-border focus:border-primary-500 focus:ring-2 focus:ring-primary-200 bg-light-background dark:bg-dark-background resize-none text-light-text-primary dark:text-dark-text-primary"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center space-x-2 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-light-surface rounded-lg disabled:opacity-70 hover:shadow-lg transition-all"
                >
                  {isSubmitting ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <span>Submit</span>
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 12h14M12 5l7 7-7 7"
                        />
                      </svg>
                    </>
                  )}
                </button>
              </form>

              <p className="mt-4 text-center text-xs text-light-text-secondary dark:text-dark-text-secondary">
                🔒 Protected by SSL encryption. Zero spam.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={closeModal}
          />
          <div className="relative bg-light-surface dark:bg-dark-surface rounded-xl p-6 shadow-xl z-10 max-w-sm mx-auto border border-light-border dark:border-dark-border">
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-light-text-secondary dark:text-dark-text-secondary"
            >
              <FaTimes />
            </button>
            <div className="text-center mt-2">
              <FaCheck className="text-primary-500 text-4xl mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2 text-light-text-primary dark:text-dark-text-primary">
                Thank You!
              </h3>
              <p className="text-light-text-secondary dark:text-dark-text-secondary">
                Your quote request has been submitted successfully. We will get
                back to you shortly.
              </p>
              <button
                onClick={closeModal}
                className="mt-6 w-full py-2 bg-gradient-to-r from-primary-500 to-secondary-500 text-light-surface rounded-lg hover:shadow-md transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ContactSection;
